import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { logError } from "@/lib/logger";
import { jobStatusColor } from "@/lib/ui";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import {
  BarChart3, FileText, Eye, Users, Radar, Loader2, CheckCircle2, Clock,
  Megaphone, Send, Hash, XCircle, Trophy, TrendingUp, Vote, Flame, Radio, RotateCw,
  Heart, Repeat2, Code2, Check,
} from "lucide-react";

const StatCard = ({ icon: Icon, label, value, accent = "#D92D20", testid }) => (
  <div className="panel p-6" data-testid={testid}>
    <div className="flex items-center justify-between">
      <span className="meta-label text-[#737373]">{label}</span>
      <Icon className="h-5 w-5" strokeWidth={1.5} style={{ color: accent }} />
    </div>
    <div className="mt-4 font-heading text-4xl text-white">{value}</div>
  </div>
);

const ChannelPill = ({ active, label, testid }) => (
  <span
    data-testid={testid}
    className="flex items-center gap-1.5 border px-2.5 py-1 font-mono text-[0.65rem] uppercase tracking-wider"
    style={{
      borderColor: active ? "#22C55E55" : "#333",
      color: active ? "#22C55E" : "#737373",
    }}
  >
    {active ? <CheckCircle2 className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}
    {label}
  </span>
);

const DigestPanel = () => {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [posting, setPosting] = useState(false);
  const [autoPost, setAutoPost] = useState(null);

  const loadPreview = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/digest/preview");
      setPreview(data);
    } catch (err) {
      logError("Failed to load digest preview:", err);
      setPreview(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPreview();
    (async () => {
      try {
        const { data } = await api.get("/admin/settings");
        setAutoPost(data.auto_post_discoveries);
      } catch (err) {
        logError("Failed to load settings:", err);
      }
    })();
  }, []);

  const toggleAutoPost = async (val) => {
    setAutoPost(val);
    try {
      await api.put("/admin/settings", { auto_post_discoveries: val });
      toast.success(val ? "Auto-posting new discoveries ON" : "Auto-posting OFF");
    } catch (err) {
      logError("Failed to update settings:", err);
      setAutoPost(!val);
      toast.error("Couldn't update setting");
    }
  };

  const postNow = async () => {
    setPosting(true);
    try {
      const { data } = await api.post("/admin/digest/post");
      const parts = [
        channelResultLabel(data.telegram, "Telegram"),
        channelResultLabel(data.twitter, "Twitter"),
      ];
      toast.success(`Digest posted: ${parts.join(" \u00B7 ")}`);
    } catch (err) {
      logError("Digest post failed:", err);
      toast.error(err?.response?.data?.detail || "Could not post digest.");
    } finally {
      setPosting(false);
    }
  };

  return (
    <div className="panel mt-8 p-6" data-testid="digest-panel">
      <div className="flex flex-wrap items-center justify-between gap-4 border-b border-[#333] pb-4">
        <div className="flex items-center gap-2">
          <Megaphone className="h-5 w-5 text-[#FBBF24]" strokeWidth={1.5} />
          <span className="font-heading text-xl text-white">Daily Digest</span>
          <span className="meta-label ml-2 text-[#737373]">Auto-posts after 06:00 UTC sweep</span>
        </div>
        <div className="flex items-center gap-2">
          <ChannelPill active={preview?.channels?.telegram} label="Telegram" testid="channel-telegram" />
          <ChannelPill active={preview?.channels?.twitter} label="Twitter/X" testid="channel-twitter" />
        </div>
      </div>

      <label className="mt-4 flex items-center gap-3 border-b border-[#333] pb-4" data-testid="auto-post-toggle">
        <Switch checked={!!autoPost} onCheckedChange={toggleAutoPost} data-testid="auto-post-switch" />
        <span className="meta-label text-[#A3A3A3]">Auto-post each newly discovered dossier to your channels</span>
      </label>

      {loading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="h-5 w-5 animate-spin text-[#FBBF24]" />
        </div>
      ) : !preview ? (
        <p className="meta-label py-6 text-[#737373]">No topic available to feature yet.</p>
      ) : (
        <div className="mt-5 grid grid-cols-1 gap-6 lg:grid-cols-2">
          <div data-testid="digest-feature">
            <div className="meta-label mb-2 text-[#A3A3A3]">Today's featured controversy</div>
            <div className="font-heading text-2xl leading-tight text-white">{preview.title}</div>
            <div className="meta-label mt-2 flex items-center gap-3 text-[#737373]">
              <span>{preview.category}</span>
              <span className="text-[#D92D20]">&#9888; {preview.controversy_score}/100</span>
            </div>
            <div className="mt-4 border-l-2 border-[#FBBF24] pl-3">
              <div className="meta-label mb-1 flex items-center gap-1.5 text-[#FBBF24]">
                <Hash className="h-3 w-3" /> AI hook
              </div>
              <p className="font-body text-[#E5E5E5]">{preview.hook}</p>
            </div>
            <button
              onClick={postNow}
              disabled={posting}
              data-testid="post-digest-btn"
              className="btn-pill mt-6 inline-flex items-center gap-2 bg-[#D92D20] text-white hover:bg-[#b8241a] disabled:opacity-60"
            >
              {posting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              {posting ? "Posting\u2026" : "Post digest now"}
            </button>
          </div>

          <div>
            <div className="meta-label mb-2 text-[#A3A3A3]">Telegram preview</div>
            <pre
              data-testid="digest-telegram-preview"
              className="whitespace-pre-wrap break-words border border-[#333] bg-[#0d0d0d] p-3 font-mono text-xs text-[#D4D4D4]"
            >{preview.posts?.telegram}</pre>
            <div className="meta-label mb-2 mt-4 text-[#A3A3A3]">Twitter / X preview</div>
            <pre
              data-testid="digest-twitter-preview"
              className="whitespace-pre-wrap break-words border border-[#333] bg-[#0d0d0d] p-3 font-mono text-xs text-[#D4D4D4]"
            >{preview.posts?.twitter}</pre>
          </div>
        </div>
      )}
    </div>
  );
};

const statusColor = jobStatusColor;

const StatusChip = ({ status, link, label }) => {
  const color = status === "ok" ? "#22C55E" : status === "skipped" ? "#737373" : "#D92D20";
  const inner = (
    <span className="flex items-center gap-1 font-mono text-[0.65rem] uppercase" style={{ color }}>
      {label}: {status === "ok" ? "\u2713" : status === "skipped" ? "\u2013" : "\u2717"}
    </span>
  );
  return link && status === "ok" ? (
    <a href={link} target="_blank" rel="noopener noreferrer" className="underline-offset-2 hover:underline">
      {inner}
    </a>
  ) : inner;
};

const PAGE = 15;

const PostingHistory = () => {
  const [posts, setPosts] = useState(null);
  const [filter, setFilter] = useState("all");
  const [resending, setResending] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const load = async (f = filter, skip = 0) => {
    try {
      const q = f === "failed" ? "&status=failed" : "";
      const { data } = await api.get(`/admin/posts?limit=${PAGE}&skip=${skip}${q}`);
      setHasMore(data.length === PAGE);
      setPosts((prev) => (skip === 0 ? data : [...(prev || []), ...data]));
    } catch (err) {
      logError("Failed to load posting history:", err);
      setPosts([]);
      setHasMore(false);
    }
  };

  useEffect(() => {
    load("all", 0);
  }, []);

  const changeFilter = (f) => {
    setFilter(f);
    setPosts(null);
    load(f, 0);
  };

  const loadMore = async () => {
    setLoadingMore(true);
    await load(filter, posts?.length || 0);
    setLoadingMore(false);
  };

  const refreshMetrics = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.post("/admin/posts/refresh-metrics");
      toast.success(`Refreshed X metrics for ${data.updated}/${data.checked} tweets`);
      await load(filter, 0);
    } catch (err) {
      logError("Refresh metrics failed:", err);
      toast.error(err?.response?.data?.detail || "Could not refresh metrics.");
    } finally {
      setRefreshing(false);
    }
  };

  const resend = async (id) => {
    setResending(id);
    try {
      const { data } = await api.post(`/admin/posts/${id}/resend`);
      const parts = [
        channelResultLabel(data.telegram, "Telegram"),
        channelResultLabel(data.twitter, "Twitter"),
      ];
      toast.success(`Resent: ${parts.join(" \u00B7 ")}`);
      await load();
    } catch (err) {
      logError("Resend failed:", err);
      toast.error(err?.response?.data?.detail || "Could not resend.");
    } finally {
      setResending(null);
    }
  };

  const filterBtn = (key, label) => (
    <button
      data-testid={`history-filter-${key}`}
      onClick={() => changeFilter(key)}
      className={`font-mono text-xs uppercase tracking-wider transition-colors ${
        filter === key ? "text-[#22C55E]" : "text-[#737373] hover:text-white"
      }`}
    >
      {label}
    </button>
  );

  return (
    <div className="panel mt-8 p-6" data-testid="posting-history">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3 border-b border-[#333] pb-4">
        <div className="flex items-center gap-2">
          <Radio className="h-5 w-5 text-[#22C55E]" strokeWidth={1.5} />
          <span className="font-heading text-xl text-white">Posting History</span>
          <span className="meta-label ml-2 text-[#737373]">Auto &amp; manual posts to your channels</span>
        </div>
        <div className="flex items-center gap-4">
          <button
            data-testid="refresh-metrics-btn"
            onClick={refreshMetrics}
            disabled={refreshing}
            className="flex items-center gap-1.5 font-mono text-xs uppercase tracking-wider text-[#FBBF24] transition-colors hover:text-white disabled:opacity-60"
          >
            {refreshing ? <Loader2 className="h-3 w-3 animate-spin" /> : <TrendingUp className="h-3 w-3" />} Refresh X metrics
          </button>
          {filterBtn("all", "All")}
          {filterBtn("failed", "Failed")}
        </div>
      </div>
      {posts === null ? (
        <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-[#22C55E]" /></div>
      ) : posts.length === 0 ? (
        <p className="meta-label py-6 text-[#737373]">{filter === "failed" ? "No failed posts \u2014 all clear." : "No posts yet."}</p>
      ) : (
        <div className="space-y-2" data-testid="posting-history-list">
          {posts.map((p, idx) => {
            const hasFailure = p.telegram.status === "failed" || p.twitter.status === "failed";
            return (
              <div key={p.id || `${p.kind}-${p.created_at}-${idx}`} className="panel flex flex-wrap items-center justify-between gap-3 p-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-[0.6rem] uppercase tracking-wider text-[#FBBF24]">{p.kind}</span>
                    <span className="meta-label text-[#737373]">{(p.created_at || "").slice(0, 10)}</span>
                    {p.source && <span className="meta-label text-[#555]">· {p.source}</span>}
                  </div>
                  <div className="mt-1 truncate font-body text-sm text-white">{p.title || "—"}</div>
                  {p.twitter.metrics && (
                    <div className="mt-1.5 flex items-center gap-3" data-testid={`metrics-${p.id}`}>
                      <span className="flex items-center gap-1 font-mono text-[0.65rem] text-[#D92D20]"><Heart className="h-3 w-3" /> {p.twitter.metrics.likes}</span>
                      <span className="flex items-center gap-1 font-mono text-[0.65rem] text-[#22C55E]"><Repeat2 className="h-3 w-3" /> {p.twitter.metrics.retweets}</span>
                      <span className="flex items-center gap-1 font-mono text-[0.65rem] text-[#737373]"><Eye className="h-3 w-3" /> {p.twitter.metrics.impressions}</span>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-4">
                  <StatusChip status={p.telegram.status} link={p.telegram.link} label="TG" />
                  <StatusChip status={p.twitter.status} link={p.twitter.link} label="X" />
                  {hasFailure && p.id && (
                    <button
                      data-testid={`resend-${p.id}`}
                      onClick={() => resend(p.id)}
                      disabled={resending === p.id}
                      className="flex items-center gap-1 border border-[#D92D20]/40 px-2 py-1 font-mono text-[0.6rem] uppercase tracking-wider text-[#D92D20] transition-colors hover:bg-[#D92D20]/10 disabled:opacity-60"
                    >
                      {resending === p.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <RotateCw className="h-3 w-3" />} Resend
                    </button>
                  )}
                </div>
              </div>
            );
          })}
          {hasMore && (
            <button
              data-testid="posting-history-load-more"
              onClick={loadMore}
              disabled={loadingMore}
              className="btn-pill mt-4 border border-[#333] text-[#A3A3A3] hover:border-[#22C55E]/50 hover:text-white disabled:opacity-60"
            >
              {loadingMore ? <Loader2 className="mr-1 inline h-3 w-3 animate-spin" /> : null} Load more
            </button>
          )}
        </div>
      )}
    </div>
  );
};

const HotDebatePanel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [posting, setPosting] = useState(false);
  const [copied, setCopied] = useState(false);

  const embedSnippet = `<iframe src="${API}/embed/hot-debate" width="100%" height="240" style="border:0;max-width:520px" loading="lazy" title="The Archive — Debate of the Week"></iframe>`;

  const copyEmbed = async () => {
    try {
      await navigator.clipboard.writeText(embedSnippet);
      setCopied(true);
      toast.success("Embed code copied — paste it into any site");
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      logError("Copy embed failed:", err);
      toast.error("Couldn't copy embed code");
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/hotdebate/preview");
      setData(data);
    } catch (err) {
      logError("Failed to load hot debate preview:", err);
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const postNow = async () => {
    setPosting(true);
    try {
      const { data } = await api.post("/admin/hotdebate/post");
      const parts = [
        channelResultLabel(data.telegram, "Telegram"),
        channelResultLabel(data.twitter, "Twitter"),
      ];
      toast.success(`Hot debate posted: ${parts.join(" \u00B7 ")}`);
    } catch (err) {
      logError("Hot debate post failed:", err);
      toast.error(err?.response?.data?.detail || "Could not post hot debate.");
    } finally {
      setPosting(false);
    }
  };

  const a = data?.votes?.a || 0;
  const b = data?.votes?.b || 0;
  const total = a + b;
  const pctA = total ? Math.round((a / total) * 100) : 0;

  return (
    <div className="panel mt-8 p-6" data-testid="hotdebate-panel">
      <div className="flex flex-wrap items-center justify-between gap-4 border-b border-[#333] pb-4">
        <div className="flex items-center gap-2">
          <Flame className="h-5 w-5 text-[#D92D20]" strokeWidth={1.5} />
          <span className="font-heading text-xl text-white">Hottest Debate</span>
          <span className="meta-label ml-2 text-[#737373]">Auto-posts Fridays 07:00 UTC</span>
        </div>
        <div className="flex items-center gap-2">
          <ChannelPill active={data?.channels?.telegram} label="Telegram" testid="hotdebate-channel-telegram" />
          <ChannelPill active={data?.channels?.twitter} label="Twitter/X" testid="hotdebate-channel-twitter" />
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="h-5 w-5 animate-spin text-[#D92D20]" />
        </div>
      ) : !data ? (
        <p className="meta-label py-6 text-[#737373]">No engaged dossiers yet.</p>
      ) : (
        <div className="mt-5 grid grid-cols-1 gap-6 lg:grid-cols-2">
          <div data-testid="hotdebate-feature">
            <div className="meta-label mb-2 text-[#A3A3A3]">This week's hottest thread</div>
            <div className="font-heading text-2xl leading-tight text-white">{data.title}</div>
            <div className="meta-label mt-2 flex items-center gap-3 text-[#737373]">
              <span className="text-[#D92D20]">&#9888; {data.controversy_score}/100</span>
              <span>&#128172; {data.comment_count} comments</span>
              <span>&#128499; {total} votes</span>
            </div>
            <div className="mt-4 flex h-2 w-full overflow-hidden">
              <div className="h-2 bg-[#D92D20]" style={{ width: `${pctA}%` }} />
              <div className="h-2 bg-[#3B82F6]" style={{ width: `${100 - pctA}%` }} />
            </div>
            <button
              onClick={postNow}
              disabled={posting}
              data-testid="post-hotdebate-btn"
              className="btn-pill mt-6 inline-flex items-center gap-2 bg-[#D92D20] text-white hover:bg-[#b8241a] disabled:opacity-60"
            >
              {posting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              {posting ? "Posting\u2026" : "Post hot debate now"}
            </button>
          </div>
          <div>
            <div className="meta-label mb-2 text-[#A3A3A3]">Telegram preview</div>
            <pre
              data-testid="hotdebate-telegram-preview"
              className="whitespace-pre-wrap break-words border border-[#333] bg-[#0d0d0d] p-3 font-mono text-xs text-[#D4D4D4]"
            >{data.posts?.telegram}</pre>
            <div className="meta-label mb-2 mt-4 flex items-center gap-1.5 text-[#A3A3A3]">
              <Code2 className="h-3.5 w-3.5" /> Embeddable "Debate of the Week" widget
            </div>
            <pre
              data-testid="hotdebate-embed-snippet"
              className="whitespace-pre-wrap break-all border border-[#333] bg-[#0d0d0d] p-3 font-mono text-[0.7rem] text-[#737373]"
            >{embedSnippet}</pre>
            <button
              data-testid="copy-embed-btn"
              onClick={copyEmbed}
              className="btn-pill mt-3 inline-flex items-center gap-2 border border-[#333] text-[#A3A3A3] hover:border-[#FBBF24]/50 hover:text-white"
            >
              {copied ? <Check className="h-4 w-4 text-[#22C55E]" /> : <Code2 className="h-4 w-4" />}
              {copied ? "Copied" : "Copy embed code"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const VoteAnalytics = () => {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/vote-stats");
        setStats(data);
      } catch (err) {
        logError("Failed to load vote stats:", err);
        setStats({ total_votes: 0, by_category: [], top_divisive: [] });
      }
    })();
  }, []);

  return (
    <div className="panel mt-8 p-6" data-testid="vote-analytics">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3 border-b border-[#333] pb-4">
        <div className="flex items-center gap-2">
          <Vote className="h-5 w-5 text-[#3B82F6]" strokeWidth={1.5} />
          <span className="font-heading text-xl text-white">Vote Analytics</span>
          <span className="meta-label ml-2 text-[#737373]">Reader lean · Position A vs B</span>
        </div>
        {stats && (
          <span className="font-mono text-sm text-[#737373]">
            <span className="text-white">{stats.total_votes}</span> votes · {stats.dossiers_with_votes || 0} dossiers
          </span>
        )}
      </div>

      {stats === null ? (
        <div className="flex justify-center py-10">
          <Loader2 className="h-5 w-5 animate-spin text-[#3B82F6]" />
        </div>
      ) : stats.total_votes === 0 ? (
        <p className="meta-label py-6 text-[#737373]">No votes cast yet.</p>
      ) : (
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
          <div>
            <div className="meta-label mb-4 text-[#A3A3A3]">Lean by category</div>
            <div className="space-y-3">
              {stats.by_category.map((c) => {
                const total = c.a + c.b;
                const pctA = total ? Math.round((c.a / total) * 100) : 0;
                return (
                  <div key={c.category}>
                    <div className="mb-1 flex items-center justify-between font-mono text-xs">
                      <span className="text-[#A3A3A3]">{c.category}</span>
                      <span className="text-[#737373]">{total}</span>
                    </div>
                    <div className="flex h-2 w-full overflow-hidden">
                      <div className="h-2 bg-[#D92D20]" style={{ width: `${pctA}%` }} />
                      <div className="h-2 bg-[#3B82F6]" style={{ width: `${100 - pctA}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div>
            <div className="meta-label mb-4 text-[#A3A3A3]">Most divisive dossiers</div>
            <div className="space-y-2" data-testid="top-divisive-list">
              {stats.top_divisive.map((t) => {
                const total = t.a + t.b;
                const pctA = total ? Math.round((t.a / total) * 100) : 0;
                return (
                  <div key={t.id} className="panel p-3">
                    <div className="mb-2 flex items-center justify-between gap-3">
                      <span className="truncate font-body text-sm text-white">{t.title}</span>
                      <span className="shrink-0 font-mono text-xs text-[#737373]">{total}</span>
                    </div>
                    <div className="flex h-1.5 w-full overflow-hidden">
                      <div className="h-1.5 bg-[#D92D20]" style={{ width: `${pctA}%` }} />
                      <div className="h-1.5 bg-[#3B82F6]" style={{ width: `${100 - pctA}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const channelResultLabel = (res, name) => {
  if (res?.ok) return `${name} \u2713`;
  if (res?.skipped) return `${name} skipped`;
  return `${name} failed`;
};

const ActivityChart = () => {
  const [series, setSeries] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/timeseries?days=30");
        setSeries(data.series.map((d) => ({ ...d, label: d.date.slice(5) })));
      } catch (err) {
        logError("Failed to load activity timeseries:", err);
        setSeries([]);
      }
    })();
  }, []);

  return (
    <div className="panel mt-8 p-6" data-testid="activity-chart">
      <div className="mb-6 flex items-center gap-2 border-b border-[#333] pb-4">
        <TrendingUp className="h-5 w-5 text-[#22C55E]" strokeWidth={1.5} />
        <span className="font-heading text-xl text-white">Activity</span>
        <span className="meta-label ml-2 text-[#737373]">Reads &amp; discoveries · last 30 days</span>
      </div>
      {series === null ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-5 w-5 animate-spin text-[#22C55E]" />
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={260}>
          <AreaChart data={series} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
            <defs>
              <linearGradient id="gReads" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#FBBF24" stopOpacity={0.5} />
                <stop offset="100%" stopColor="#FBBF24" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="gDisc" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#D92D20" stopOpacity={0.5} />
                <stop offset="100%" stopColor="#D92D20" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#222" vertical={false} />
            <XAxis dataKey="label" stroke="#737373" tick={{ fontSize: 10, fontFamily: "monospace" }} interval={4} />
            <YAxis stroke="#737373" tick={{ fontSize: 10, fontFamily: "monospace" }} allowDecimals={false} />
            <Tooltip
              contentStyle={{ background: "#0d0d0d", border: "1px solid #333", fontFamily: "monospace", fontSize: 12 }}
              labelStyle={{ color: "#A3A3A3" }}
            />
            <Legend wrapperStyle={{ fontFamily: "monospace", fontSize: 11 }} />
            <Area type="monotone" dataKey="reads" name="Reads" stroke="#FBBF24" fill="url(#gReads)" strokeWidth={2} />
            <Area type="monotone" dataKey="discoveries" name="Discoveries" stroke="#D92D20" fill="url(#gDisc)" strokeWidth={2} />
          </AreaChart>
        </ResponsiveContainer>
      )}
    </div>
  );
};

const RecapPanel = () => {
  const [recap, setRecap] = useState(null);
  const [loading, setLoading] = useState(true);
  const [posting, setPosting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/recap/preview");
      setRecap(data);
    } catch (err) {
      logError("Failed to load recap preview:", err);
      setRecap(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const postNow = async () => {
    setPosting(true);
    try {
      const { data } = await api.post("/admin/recap/post");
      const parts = [
        channelResultLabel(data.telegram, "Telegram"),
        channelResultLabel(data.twitter, "Twitter"),
      ];
      toast.success(`Weekly recap posted: ${parts.join(" \u00B7 ")}`);
    } catch (err) {
      logError("Recap post failed:", err);
      toast.error(err?.response?.data?.detail || "Could not post recap.");
    } finally {
      setPosting(false);
    }
  };

  return (
    <div className="panel mt-8 p-6" data-testid="recap-panel">
      <div className="flex flex-wrap items-center justify-between gap-4 border-b border-[#333] pb-4">
        <div className="flex items-center gap-2">
          <Trophy className="h-5 w-5 text-[#FBBF24]" strokeWidth={1.5} />
          <span className="font-heading text-xl text-white">Weekly Top-5 Recap</span>
          <span className="meta-label ml-2 text-[#737373]">Auto-posts Mondays 07:00 UTC</span>
        </div>
        <div className="flex items-center gap-2">
          <ChannelPill active={recap?.channels?.telegram} label="Telegram" testid="recap-channel-telegram" />
          <ChannelPill active={recap?.channels?.twitter} label="Twitter thread" testid="recap-channel-twitter" />
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="h-5 w-5 animate-spin text-[#FBBF24]" />
        </div>
      ) : !recap ? (
        <p className="meta-label py-6 text-[#737373]">No read dossiers yet to recap.</p>
      ) : (
        <div className="mt-5 grid grid-cols-1 gap-6 lg:grid-cols-2">
          <div data-testid="recap-list">
            <div className="meta-label mb-3 text-[#A3A3A3]">This week's most-read</div>
            <div className="space-y-2">
              {recap.topics.map((t, i) => (
                <div key={t.id} className="panel flex items-center justify-between p-3">
                  <div className="flex min-w-0 items-center gap-3">
                    <span className="font-mono text-lg text-[#737373]">{String(i + 1).padStart(2, "0")}</span>
                    <div className="min-w-0">
                      <div className="truncate font-body text-white">{t.title}</div>
                      <div className="meta-label mt-0.5 text-[#737373]">
                        {t.category} &middot; &#9888; {t.controversy_score}/100
                        {t.verified_count > 0 ? ` \u00B7 \uD83D\uDD12 ${t.verified_count} verified` : ""}
                      </div>
                    </div>
                  </div>
                  <span className="ml-3 flex shrink-0 items-center gap-1.5 font-mono text-sm text-[#FBBF24]">
                    <Eye className="h-3.5 w-3.5" strokeWidth={1.5} /> {t.views}
                  </span>
                </div>
              ))}
            </div>
            <button
              onClick={postNow}
              disabled={posting}
              data-testid="post-recap-btn"
              className="btn-pill mt-6 inline-flex items-center gap-2 bg-[#D92D20] text-white hover:bg-[#b8241a] disabled:opacity-60"
            >
              {posting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              {posting ? "Posting\u2026" : "Post weekly recap now"}
            </button>
          </div>

          <div>
            <div className="meta-label mb-2 text-[#A3A3A3]">Telegram preview</div>
            <pre
              data-testid="recap-telegram-preview"
              className="whitespace-pre-wrap break-words border border-[#333] bg-[#0d0d0d] p-3 font-mono text-xs text-[#D4D4D4]"
            >{recap.posts?.telegram}</pre>
            <div className="meta-label mb-2 mt-4 text-[#A3A3A3]">Twitter thread preview ({recap.posts?.twitter?.length} tweets)</div>
            <div className="space-y-2">
              {recap.posts?.twitter?.map((tw, i) => (
                <pre
                  key={i}
                  className="whitespace-pre-wrap break-words border border-[#333] bg-[#0d0d0d] p-3 font-mono text-xs text-[#D4D4D4]"
                >{tw}</pre>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default function Dashboard() {
  const { user, checked } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (checked && (!user || user.role !== "admin")) {
      navigate("/");
    }
  }, [user, checked, navigate]);

  useEffect(() => {
    if (!user || user.role !== "admin") return;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/admin/stats");
        setStats(data);
      } catch (err) {
        logError("Failed to load admin stats:", err);
      } finally {
        setLoading(false);
      }
    })();
  }, [user]);

  if (loading || !stats) {
    return (
      <div className="flex justify-center py-32">
        <Loader2 className="h-7 w-7 animate-spin text-[#D92D20]" />
      </div>
    );
  }

  const maxCat = Math.max(...stats.categories.map((c) => c.count), 1);

  return (
    <div className="mx-auto max-w-7xl px-6 py-12" data-testid="admin-dashboard">
      <div className="mb-10 flex items-center gap-3 border-b border-[#333] pb-6">
        <BarChart3 className="h-6 w-6 text-[#D92D20]" strokeWidth={1.5} />
        <div>
          <h1 className="font-heading text-3xl tracking-tight text-white">Archivist Dashboard</h1>
          <div className="meta-label mt-1 text-[#737373]">Admin / At-a-glance intelligence</div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard icon={FileText} label="Total dossiers" value={stats.total_topics} testid="stat-total" />
        <StatCard icon={CheckCircle2} label="Analyzed" value={stats.analyzed_topics} accent="#22C55E" testid="stat-analyzed" />
        <StatCard icon={Eye} label="Total reads" value={stats.total_views} accent="#FBBF24" testid="stat-views" />
        <StatCard icon={Users} label="Investigators" value={stats.total_users} accent="#3B82F6" testid="stat-users" />
      </div>

      <ActivityChart />
      <VoteAnalytics />
      <DigestPanel />
      <RecapPanel />
      <HotDebatePanel />
      <PostingHistory />

      <div className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-3">
        {/* Top read */}
        <div className="lg:col-span-2">
          <div className="meta-label mb-4 flex items-center gap-2 text-[#A3A3A3]">
            <Eye className="h-4 w-4 text-[#FBBF24]" strokeWidth={1.5} /> Most-read controversies
          </div>
          {stats.top_read.length === 0 ? (
            <p className="meta-label text-[#737373]">No reads recorded yet.</p>
          ) : (
            <div className="space-y-2" data-testid="top-read-list">
              {stats.top_read.map((t, i) => (
                <div
                  key={t.id}
                  onClick={() => navigate(`/topic/${t.id}`)}
                  className="panel flex cursor-pointer items-center justify-between p-4 transition-colors hover:border-[#FBBF24]/50"
                >
                  <div className="flex items-center gap-4">
                    <span className="font-mono text-lg text-[#737373]">{String(i + 1).padStart(2, "0")}</span>
                    <div>
                      <div className="font-body text-white">{t.title}</div>
                      <div className="meta-label mt-1 text-[#737373]">{t.category}</div>
                    </div>
                  </div>
                  <span className="flex items-center gap-1.5 font-mono text-sm text-[#FBBF24]">
                    <Eye className="h-3.5 w-3.5" strokeWidth={1.5} /> {t.views}
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* Categories */}
          <div className="meta-label mb-4 mt-10 flex items-center gap-2 text-[#A3A3A3]">
            <FileText className="h-4 w-4 text-[#D92D20]" strokeWidth={1.5} /> Dossiers by category
          </div>
          <div className="space-y-3">
            {stats.categories.map((c) => (
              <div key={c.category} className="flex items-center gap-3">
                <span className="w-28 shrink-0 font-mono text-xs text-[#A3A3A3]">{c.category}</span>
                <div className="h-2 flex-1 bg-[#1a1a1a]">
                  <div className="h-2 bg-[#D92D20]" style={{ width: `${(c.count / maxCat) * 100}%` }} />
                </div>
                <span className="w-8 text-right font-mono text-xs text-[#737373]">{c.count}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Discovery activity */}
        <div>
          <div className="meta-label mb-4 flex items-center gap-2 text-[#A3A3A3]">
            <Radar className="h-4 w-4 text-[#22C55E]" strokeWidth={1.5} /> Discovery activity
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="panel p-4 text-center">
              <div className="font-heading text-3xl text-white">{stats.discovery.jobs_24h}</div>
              <div className="meta-label mt-1 text-[#737373]">Last 24h</div>
            </div>
            <div className="panel p-4 text-center">
              <div className="font-heading text-3xl text-white">{stats.discovery.jobs_7d}</div>
              <div className="meta-label mt-1 text-[#737373]">Last 7d</div>
            </div>
          </div>

          <div className="meta-label mb-3 mt-8 text-[#A3A3A3]">Recent investigations</div>
          <div className="space-y-2" data-testid="recent-jobs">
            {stats.discovery.recent.length === 0 ? (
              <p className="meta-label text-[#737373]">No investigations yet.</p>
            ) : (
              stats.discovery.recent.map((j, i) => (
                <div key={`${j.created_at}-${i}`} className="panel flex items-center justify-between p-3">
                  <div className="min-w-0">
                    <div className="truncate font-mono text-xs text-[#D4D4D4]">
                      {j.query || "Trending sweep"}
                    </div>
                    <div className="meta-label mt-1 flex items-center gap-1.5 text-[#737373]">
                      <Clock className="h-3 w-3" /> {(j.created_at || "").slice(0, 16).replace("T", " ")}
                    </div>
                  </div>
                  <span className="ml-3 shrink-0 font-mono text-[0.65rem] uppercase" style={{ color: statusColor(j.status) }}>
                    {j.status}{j.status === "done" ? ` · ${j.count}` : ""}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
